import { usePresenceStore } from '../stores/usePresenceStore';
import { useCallback, useEffect } from 'react';
import type { ChannelMember } from '../lib/api';
import { useAuthStore } from '../stores/useAuthStore';
import { useChannelStore } from '../stores/useChannelStore';
import { useDmStore } from '../stores/useDmStore';
import { channelRosterAdmission, useChannelMemberStore, type ChannelRosterResult } from '../stores/useChannelMemberStore';

const EMPTY_MEMBERS: ChannelMember[] = [];

export interface ChannelMembers {
  status: 'unavailable' | 'idle' | 'loading' | 'ready' | 'error';
  members: ChannelMember[];
  error: Error | null;
  reload: () => Promise<ChannelRosterResult>;
}

/** Optional roster enrichment is unavailable to previews, independently of their readable content. */
export const useChannelMembers = (
  channelId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): ChannelMembers => {
  const outputEpoch = usePresenceStore(state => state.providerOutputEpoch);
  const connectionState = usePresenceStore(state => state.connectionState);
  const accountId = useAuthStore((state) => state.currentUser?.id);
  useChannelStore((state) => state.channels);
  useDmStore((state) => state.dmChannels);
  useChannelMemberStore((state) => state.threadChannelsById);
  useChannelMemberStore((state) => state.threadRosterProofById);
  const version = useChannelStore((state) => channelId ? state.channelMembersVersion[channelId] ?? 0 : 0);
  const members = useChannelMemberStore((state) => channelId ? state.byChannelId[channelId] : undefined);
  const loadedVersion = useChannelMemberStore((state) => channelId ? state.loadedVersionByChannelId[channelId] : undefined);
  const loading = useChannelMemberStore((state) => channelId ? state.loadingByChannelId[channelId] === true : false);
  const error = useChannelMemberStore((state) => channelId ? state.errorsByChannelId[channelId] : undefined);
  const load = useChannelMemberStore((state) => state.loadChannelMembers);
  const admission = channelRosterAdmission(channelId);
  const available = admission.available;
  const reload = useCallback((): Promise<ChannelRosterResult> => {
    if (!channelId) return Promise.resolve({ status: 'unavailable', reason: 'unknown-conversation' });
    return load(channelId, version);
  }, [channelId, version, accountId, load, outputEpoch]);

  useEffect(() => {
    if (!enabled || !available || (outputEpoch > 0 && connectionState !== 'connected')) return;
    // Admitted errors remain in the shared error state and are exposed by this hook.
    void reload().catch(() => {});
  }, [enabled, available, reload, connectionState, outputEpoch]);

  if (!available) return { status: 'unavailable', members: EMPTY_MEMBERS, error: null, reload };
  const status = error ? 'error' : loading ? 'loading' : loadedVersion !== undefined && loadedVersion >= version ? 'ready' : 'idle';
  return { status, members: members ?? EMPTY_MEMBERS, error: error ?? null, reload };
};
